import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { I18N_NS } from "Config/i18n";
import { Row, Dropdown, message, Tooltip, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "Components/General/Button";
import { investigationsLabelsStore } from "../Stores/InvestigationsLabelsStore";
import { ModalComponentProps, withModal } from "Components/General/Modal/ModalContext";
import { WithModal } from "Components/General/Modal/withModal";
import { useEffect, useState } from "react";
import { notificationError, notificationSuccess } from "utils/libHelpers/AntdNotification";

interface InvestSearchQueriesProps extends WithTranslation, WithModal {
  style?: React.CSSProperties;
}

interface InvestSearchQueriesState {
  queryName: string;
  visible: boolean;
}

export const InvestSearchQueries = withModal(
  withTranslation(I18N_NS.PROJECT)(
    observer(
      class extends React.Component<InvestSearchQueriesProps, InvestSearchQueriesState> {
        private MODAL_PROPS = { width: `100%`, style: { maxWidth: 400, top: 100 }, footer: null };

        constructor(params: InvestSearchQueriesProps) {
          super(params);
          this.state = {
            queryName: "",
            visible: false,
          };
        }

        handleExecuteQuery = async ({ key: id }) => {
          if (id !== "SAVE") {
            const hide = message.loading(this.props.t("Loading..."), 0);
            try {
              await investigationsLabelsStore.fetch(id);
            } catch (error) {
              throw error;
            } finally {
              hide();
            }
          }
        };

        handleVisibleChange = (visible: boolean) => {
          this.setState({ visible });
        };

        renderSaveButton = () => {
          const { t } = this.props;

          return (
            <Row justify="center">
              <Button
                size="small"
                type="primary"
                style={{
                  margin: "8px 8px",
                }}
                onClick={() =>
                  this.props.showModal<Partial<InvestSearchQueryFormProps>>(InvestSearchQueryForm, {
                    modalProps: { ...this.MODAL_PROPS, title: t("Save query") },
                  })
                }
              >
                {t("Save query")}
              </Button>
            </Row>
          );
        };

        get queriesItems() {
          const data = investigationsLabelsStore.queries;
          const { t } = this.props;
          return data.map((item) => ({
            key: `${item.id}`,
            label: (
              <Row justify="space-between">
                <Button
                  size="small"
                  style={{
                    marginRight: "10px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    maxWidth: 400,
                  }}
                >
                  {item.queryName}
                </Button>
                <div>
                  <span
                    style={{ marginRight: "10px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      this.props.showModal<Partial<InvestSearchQueryFormProps>>(InvestSearchQueryForm, {
                        modalProps: { ...this.MODAL_PROPS, title: t("Edit query") },
                        componentProps: { initialData: item },
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={["fas", "edit"]} size="1x" fixedWidth={true} />
                  </span>

                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      investigationsLabelsStore.deleteQuery(item.id);
                    }}
                  >
                    <FontAwesomeIcon icon={["fas", "trash"]} size="1x" fixedWidth={true} />
                  </span>
                </div>
              </Row>
            ),
          }));
        }

        render() {
          return (
            <>
              <Tooltip title={this.props.t("My saved queries")} placement="topRight">
                <Dropdown
                  menu={{
                    items: this.queriesItems,
                    style: { maxHeight: "300px", overflowY: "auto" },
                    onClick: this.handleExecuteQuery,
                  }}
                  popupRender={(menu) => (
                    <>
                      {investigationsLabelsStore.queries.length ? (
                        menu
                      ) : (
                        <div className="ant-dropdown-menu" style={{ padding: 10 }}>
                          {this.renderSaveButton()}
                          {this.props.t("No saved queries")}
                        </div>
                      )}
                    </>
                  )}
                  trigger={["click"]}
                >
                  <div className="searchbar-options star">
                    <FontAwesomeIcon icon={["far", "star"]} size="1x" fixedWidth={true} />
                  </div>
                </Dropdown>
              </Tooltip>
            </>
          );
        }
      }
    )
  )
);

interface InvestSearchQueryFormProps extends WithTranslation, ModalComponentProps {
  initialData?: API_FileDataLabel;
}

export const InvestSearchQueryForm = withTranslation(I18N_NS.PROJECT)(
  ({ t, initialData, hideModal }: InvestSearchQueryFormProps) => {
    const [name, setName] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const handleSubmit = async () => {
      try {
        setLoading(true);
        if (initialData) {
          await investigationsLabelsStore.editQuery({ ...initialData, queryName: name });
        } else {
          await investigationsLabelsStore.saveQuery(name);
        }
        notificationSuccess(t("Success"), t("Query was saved"));
        hideModal();
      } catch (error) {
        notificationError(t("Error"), t("Query was not saved"));
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (initialData) {
        setName(initialData.queryName);
      }
    }, [initialData]);

    return (
      <div>
        <Row justify="start" align="middle" className="mb-lg">
          <div className="mr-sm">{t("Name")}:</div>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="full-width flex1" />
        </Row>
        <Row justify="end">
          <Button
            type="primary"
            style={{
              margin: "2px 8px",
            }}
            loading={loading}
            onClick={handleSubmit}
          >
            {initialData ? t("Edit") : t("Save")}
          </Button>
        </Row>
      </div>
    );
  }
);
